import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AgentMode } from "@/components/project/ModeSelector";
import { track } from "@/lib/analytics";
import {
  AssistantRuntimeProvider,
  useLocalRuntime,
  ExportedMessageRepository,
  SimpleImageAttachmentAdapter,
  type ChatModelAdapter,
  type ChatModelRunOptions,
} from "@assistant-ui/react";
import type {
  TextMessagePart,
  ToolCallMessagePart,
  ThreadAssistantMessagePart,
} from "@assistant-ui/react";
import { useAuth } from "@clerk/react";
import { Thread } from "@/components/assistant-ui/thread";
import { AssetPickerSheet } from "@/components/assets/AssetPickerSheet";
import { PipelineToolUIs } from "@/components/assistant-ui/tools";
import { createAssetsApi } from "@/lib/api/assets";
import { usePipelineStore } from "@/lib/stores/pipeline-store";
import { useExecutionStore } from "@/lib/stores/execution-store";
import { createProjectsApi, type Project } from "@/lib/api/projects";
import {
  streamChatEvents,
  streamReplayChatEvents,
  getChatHistory,
  type ChatAttachmentImageRef,
  type HistoryToolCallPart,
  type HistoryAttachment,
} from "@/lib/api/chat";
import type { Asset } from "@/lib/types/assets";
import type { PipelineNode, PipelineEdge } from "@/lib/types/pipeline";
import type { RunResponse } from "@/lib/api/runs";

interface ChatPanelProps {
  project: Project;
  initialAttachmentImage?: ChatAttachmentImageRef | null;
  initialMode?: string | null;
}

interface SystemNoticeMessagePart {
  type: "system-notice";
  message: string;
}

type ContentPart =
  | TextMessagePart
  | ToolCallMessagePart
  | SystemNoticeMessagePart;

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("Failed to read image"));
    reader.readAsDataURL(blob);
  });

export function ChatPanel({
  project,
  initialAttachmentImage = null,
  initialMode = null,
}: ChatPanelProps) {
  const { getToken } = useAuth();
  const assetsApi = useMemo(() => createAssetsApi({ getToken }), [getToken]);
  const attachmentAdapter = useMemo(
    () => new SimpleImageAttachmentAdapter(),
    [],
  );

  // Track pending background run (fired before page loaded) and initial message
  const pendingRunIdRef = useRef<string | null>(
    project.pendingChatRunId ?? null,
  );
  const initialMessageRef = useRef<string | null>(
    project.initialMessage ?? project.description ?? null,
  );
  const initialAttachmentImageRef = useRef<ChatAttachmentImageRef | null>(
    initialAttachmentImage,
  );

  // Pipeline store — raw actions (no isDirty side effect)
  const addNodeRaw = usePipelineStore((s) => s.addNodeRaw);
  const removeNodeRaw = usePipelineStore((s) => s.removeNodeRaw);
  const updateNodeRaw = usePipelineStore((s) => s.updateNodeRaw);
  const addEdgeRaw = usePipelineStore((s) => s.addEdgeRaw);
  const removeEdgeRaw = usePipelineStore((s) => s.removeEdgeRaw);
  const markClean = usePipelineStore((s) => s.markClean);

  // Execution store — to trigger run if agent calls run_pipeline
  const startRun = useExecutionStore((s) => s.startRun);
  const loadPipeline = usePipelineStore((s) => s.loadPipeline);

  const [mode, setMode] = useState<AgentMode>(
    (initialMode as AgentMode) || "auto-detect",
  );
  const [attachmentPickerOpen, setAttachmentPickerOpen] = useState(false);
  const [selectedAttachmentImage, setSelectedAttachmentImage] =
    useState<ChatAttachmentImageRef | null>(null);
  const [selectedAttachmentPreviewUrl, setSelectedAttachmentPreviewUrl] =
    useState<string | null>(null);

  useEffect(() => {
    if (!selectedAttachmentImage?.assetId) {
      setSelectedAttachmentPreviewUrl(null);
      return;
    }

    let objectUrl: string | null = null;
    assetsApi
      .fetchContent(selectedAttachmentImage.assetId)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setSelectedAttachmentPreviewUrl(objectUrl);
      })
      .catch(() => setSelectedAttachmentPreviewUrl(null));

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setSelectedAttachmentPreviewUrl(null);
    };
  }, [assetsApi, selectedAttachmentImage?.assetId]);

  const applyMutation = useCallback(
    (toolName: string, result: unknown) => {
      // Normalize result — backend may send a plain object, JSON string, or Python dict string
      const r = (() => {
        if (!result) return null;
        if (typeof result === "object") return result as Record<string, unknown>;
        if (typeof result === "string") {
          try { return JSON.parse(result) as Record<string, unknown>; } catch {}
          // Fallback: Python dict format (single quotes, True/False/None)
          try {
            return JSON.parse(
              result
                .replace(/'/g, '"')
                .replace(/\bTrue\b/g, "true")
                .replace(/\bFalse\b/g, "false")
                .replace(/\bNone\b/g, "null"),
            ) as Record<string, unknown>;
          } catch {}
        }
        return null;
      })();

      switch (toolName) {
        case "add_node":
          if (r?.node) addNodeRaw(r.node as PipelineNode);
          break;
        case "remove_node":
          if (r?.nodeId) removeNodeRaw(r.nodeId as string);
          break;
        case "update_node":
          if (r?.node) updateNodeRaw(r.node as PipelineNode);
          break;
        case "connect_nodes":
          if (r?.edge) addEdgeRaw(r.edge as PipelineEdge);
          break;
        case "remove_edge":
          if (r?.edgeId) removeEdgeRaw(r.edgeId as string);
          break;
        case "run_pipeline":
          if (r?.run) startRun(r.run as RunResponse);
          break;
        case "build_pipeline":
          if (Array.isArray(r?.nodes) && Array.isArray(r?.edges)) {
            loadPipeline(r.nodes as PipelineNode[], r.edges as PipelineEdge[]);
          }
          break;
      }
      // Canvas is updated immediately above via addNodeRaw/addEdgeRaw/etc.
      // We intentionally avoid queryClient.invalidateQueries here to prevent a
      // race condition where a mid-stream refetch overwrites the canvas before
      // all agent mutations have arrived.
    },
    [addNodeRaw, removeNodeRaw, updateNodeRaw, addEdgeRaw, removeEdgeRaw, startRun, loadPipeline],
  );

  // Track whether the history adapter has finished loading (null = loading, false = empty, true = has data)
  const [historyStatus, setHistoryStatus] = useState<
    "loading" | "empty" | "loaded"
  >("loading");

  // History adapter — loads Agno persisted chat history on mount.
  // Sets historyStatus to coordinate with the pending-run replay logic below.
  const historyAdapter = useMemo(
    () => ({
      async load() {
        const token = await getToken();
        if (!token) {
          setHistoryStatus("empty");
          return ExportedMessageRepository.fromArray([]);
        }
        const runs = await getChatHistory(project.id, token);
        if (runs.length > 0) {
          // History exists — clear pending replay (already stored by Agno)
          pendingRunIdRef.current = null;
          setHistoryStatus("loaded");
        } else {
          setHistoryStatus("empty");
        }
        const buildAttachments = async (atts: HistoryAttachment[]) => {
          const results = await Promise.allSettled(
            atts.map(async (att) => {
              const blob = await assetsApi.fetchContent(att.assetId);
              const dataUrl = await blobToDataUrl(blob);
              return {
                id: `att-${att.assetId}`,
                type: "image",
                name: att.name,
                contentType: att.mimeType,
                status: { type: "complete" },
                content: [{ type: "image", image: dataUrl, filename: att.name }],
              };
            }),
          );
          return results
            .filter((r) => r.status === "fulfilled")
            .map((r) => (r as PromiseFulfilledResult<any>).value);
        };

        const messages: any[] = [];
        for (const run of runs) {
          const attachments =
            run.userMessage.attachments?.length
              ? await buildAttachments(run.userMessage.attachments)
              : undefined;

          messages.push({
            role: "user" as const,
            content: [{ type: "text" as const, text: run.userMessage.content }],
            id: run.userMessage.id,
            createdAt: new Date(run.userMessage.createdAt * 1000),
            ...(attachments?.length ? { attachments: attachments as any } : {}),
          });
          const assistantContent = run.assistantParts.map((part) => {
            if (part.type === "text") {
              return { type: "text" as const, text: part.text };
            }
            const tc = part as HistoryToolCallPart;
            return {
              type: "tool-call" as const,
              toolCallId: tc.toolCallId,
              toolName: tc.toolName,
              args: tc.args,
              argsText: JSON.stringify(tc.args, null, 2),
              ...(tc.result !== undefined
                ? { result: tc.result, isError: tc.isError }
                : {}),
            };
          });
          if (assistantContent.length > 0) {
            messages.push({
              role: "assistant" as const,
              content:
                assistantContent as unknown as ThreadAssistantMessagePart[],
              id: run.runId,
              createdAt: new Date(run.createdAt * 1000),
            } as any);
          }
        }
        return ExportedMessageRepository.fromArray(messages);
      },
      async append(_item: unknown) {
        // Agno handles persistence — no-op
      },
    }),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Stable adapter ref — avoids re-creating on every render.
  // Captures latest closure values via refs accessed inside the generator.
  const adapterRef = useRef<ChatModelAdapter | null>(null);
  const stableRefs = useRef({
    getToken,
    applyMutation,
    projectId: project.id,
    markClean,
    pendingRunIdRef,
    selectedAttachmentImage,
    setSelectedAttachmentImage,
    mode,
  });
  stableRefs.current = {
    getToken,
    applyMutation,
    projectId: project.id,
    markClean,
    pendingRunIdRef,
    selectedAttachmentImage,
    setSelectedAttachmentImage,
    mode,
  };

  if (!adapterRef.current) {
    adapterRef.current = {
      async *run({ messages, abortSignal }: ChatModelRunOptions) {
        const refs = stableRefs.current;

        const token = await refs.getToken();
        if (!token) throw new Error("Not authenticated");

        track("chat_message_sent");

        // Accumulate content parts — each yield is the full current state.
        // currentTextIdx tracks which text part is being appended to; reset after
        // each tool call/result so post-tool text becomes a new segment (correct ordering).
        // Parts is typed as ContentPart[] locally; cast to ThreadAssistantMessagePart[] at
        // yield boundaries because @assistant-ui's closed union doesn't include our custom part.
        const parts: ContentPart[] = [];
        let currentTextIdx = -1;
        const yieldParts = () => ({
          content: [...parts] as unknown as ThreadAssistantMessagePart[],
        });

        const handleEvent = async function* (
          eventStream: AsyncIterable<import("@/lib/api/chat").ChatStreamEvent>,
        ) {
          for await (const event of eventStream) {
            if (event.type === "text_delta") {
              if (currentTextIdx === -1) {
                currentTextIdx = parts.length;
                parts.push({ type: "text", text: event.delta });
              } else {
                const cur = parts[currentTextIdx] as TextMessagePart;
                parts[currentTextIdx] = {
                  type: "text",
                  text: cur.text + event.delta,
                };
              }
              yield yieldParts();
            } else if (event.type === "tool_call") {
              currentTextIdx = -1;
              parts.push({
                type: "tool-call",
                toolCallId: event.toolCallId,
                toolName: event.toolName,
                args: event.args,
                argsText: JSON.stringify(event.args, null, 2),
              } as ToolCallMessagePart);
              yield yieldParts();
            } else if (event.type === "tool_result") {
              currentTextIdx = -1;
              const tcIdx = parts.findIndex(
                (p) =>
                  p.type === "tool-call" &&
                  (p as ToolCallMessagePart).toolCallId === event.toolCallId,
              );
              if (tcIdx >= 0) {
                parts[tcIdx] = {
                  ...(parts[tcIdx] as ToolCallMessagePart),
                  result: event.result,
                  isError: event.isError,
                } as ToolCallMessagePart;
              }
              yield yieldParts();
              refs.applyMutation(event.toolName, event.result);
            } else if (event.type === "system_notice") {
              currentTextIdx = -1;
              parts.push({
                type: "system-notice",
                message: event.message,
              } as SystemNoticeMessagePart);
              yield yieldParts();
            } else if (event.type === "error") {
              throw new Error(event.message);
            }
            // "done" event — the generator ends naturally
          }
        };

        // Replay a background run if pending (fired before this page loaded)
        if (refs.pendingRunIdRef.current) {
          const runId = refs.pendingRunIdRef.current;
          refs.pendingRunIdRef.current = null;
          yield* handleEvent(
            streamReplayChatEvents(refs.projectId, runId, token, abortSignal),
          );
          return;
        }

        // Normal flow — in-session message
        // Auto-save dirty graph before sending message
        const store = usePipelineStore.getState();
        if (store.isDirty) {
          const graph = { nodes: store.nodes, edges: store.edges };
          const projectsApi = createProjectsApi({ getToken: refs.getToken });
          await projectsApi.update(refs.projectId, { graph });
          refs.markClean();
        }

        const lastMessage = messages[messages.length - 1];
        const messageText = lastMessage.content
          .filter((c): c is { type: "text"; text: string } => c.type === "text")
          .map((c) => c.text)
          .join("");
        const attachmentImage = refs.selectedAttachmentImage;
        if (attachmentImage) refs.setSelectedAttachmentImage(null);

        yield* handleEvent(
          streamChatEvents(
            refs.projectId,
            { message: messageText, attachmentImage, mode: refs.mode },
            token,
            abortSignal,
          ),
        );
      },
    };
  }

  const runtime = useLocalRuntime(adapterRef.current, {
    adapters: { history: historyAdapter, attachments: attachmentAdapter },
  });

  // Auto-fire: only when history is confirmed empty (not loading, not loaded).
  // If history has data, the thread is already populated by the history adapter.
  // Calling startRun() separately would create a second assistant branch ("2/2").
  useEffect(() => {
    if (historyStatus !== "empty") return;
    if (!pendingRunIdRef.current || !initialMessageRef.current) return;
    const msg = initialMessageRef.current;
    initialMessageRef.current = null;
    let cancelled = false;

    const appendInitialMessage = async () => {
      const initialAttachment = initialAttachmentImageRef.current;
      initialAttachmentImageRef.current = null;

      let attachments: unknown[] | undefined;
      if (initialAttachment) {
        try {
          const blob = await assetsApi.fetchContent(initialAttachment.assetId);
          const dataUrl = await blobToDataUrl(blob);
          attachments = [
            {
              id: `att-${initialAttachment.assetId}`,
              type: "image",
              name: initialAttachment.name,
              contentType: initialAttachment.mimeType,
              status: { type: "complete" },
              content: [
                {
                  type: "image",
                  image: dataUrl,
                  filename: initialAttachment.name,
                },
              ],
            },
          ];
        } catch {
          attachments = undefined;
        }
      }

      if (cancelled) return;

      runtime.thread.append({
        role: "user",
        content: [{ type: "text", text: msg }],
        ...(attachments ? { attachments: attachments as any } : {}),
      });
    };

    void appendInitialMessage();
    return () => {
      cancelled = true;
    };
  }, [runtime, historyStatus, assetsApi]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectAttachmentImage = useCallback(
    async (asset: Asset) => {
      setSelectedAttachmentImage({
        assetId: asset.id,
        storageKey: asset.storageKey,
        name: asset.name,
        mimeType: asset.mimeType,
        sizeBytes: asset.sizeBytes,
      });
      setAttachmentPickerOpen(false);

      try {
        const blob = await assetsApi.fetchContent(asset.id);
        const file = new File([blob], asset.name, {
          type: asset.mimeType || blob.type || "image/*",
        });
        await runtime.thread.composer.clearAttachments();
        await runtime.thread.composer.addAttachment(file);
      } catch {
        // Keep metadata attachment for backend injection even if local preview fails.
      }
    },
    [assetsApi, runtime],
  );

  const handleClearAttachment = useCallback(() => {
    setSelectedAttachmentImage(null);
    void runtime.thread.composer.clearAttachments();
  }, [runtime]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <PipelineToolUIs />
      <Thread
        selectedAttachmentName={selectedAttachmentImage?.name ?? null}
        selectedAttachmentSizeBytes={selectedAttachmentImage?.sizeBytes ?? null}
        selectedAttachmentPreviewUrl={selectedAttachmentPreviewUrl}
        onOpenAttachmentPicker={() => setAttachmentPickerOpen(true)}
        onClearAttachment={handleClearAttachment}
        mode={mode}
        onModeChange={setMode}
      />
      <AssetPickerSheet
        open={attachmentPickerOpen}
        onOpenChange={setAttachmentPickerOpen}
        acceptType="image"
        onSelect={handleSelectAttachmentImage}
      />
    </AssistantRuntimeProvider>
  );
}
